import { useCallback, useEffect, useRef, useState } from 'react';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';
import { backendBaseUrl } from 'config.js';
import { registerPushToken, unregisterPushToken } from 'api/pushTokens';
import { useAuth } from 'contexts/AuthContext';

const pushConfigUrl = new URL('/push/firebase-config', backendBaseUrl).toString();

let firebaseAppInstance = null;
const getFirebaseAppInstance = (config) => {
  if (!firebaseAppInstance) {
    firebaseAppInstance = initializeApp(config);
  }
  return firebaseAppInstance;
};

let pushConfigPromise = null;
const fetchPushConfig = () => {
  if (!pushConfigPromise) {
    pushConfigPromise = fetch(pushConfigUrl)
      .then((res) => {
        if (!res.ok) {
          throw new Error('No se pudo obtener la configuración de Firebase');
        }
        return res.json();
      })
      .catch((err) => {
        pushConfigPromise = null;
        throw err;
      });
  }
  return pushConfigPromise;
};

const supportsPushMessaging = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  typeof Notification !== 'undefined';

export const usePushNotifications = ({ onMessageReceived } = {}) => {
  const { user, sessionReauthToken } = useAuth();
  const [pushReady, setPushReady] = useState(false);
  const registerPromiseRef = useRef(null);
  const tokenRef = useRef(null);

  const registerPush = useCallback(() => {
    if (!supportsPushMessaging()) {
      return Promise.resolve(null);
    }
    if (!user) {
      return Promise.resolve(null);
    }
    if (sessionReauthToken) {
      return Promise.resolve(null);
    }
    if (Notification.permission !== 'granted') {
      return Promise.resolve(null);
    }
    if (registerPromiseRef.current) {
      return registerPromiseRef.current;
    }
    registerPromiseRef.current = (async () => {
      const { firebaseConfig, vapidKey } = await fetchPushConfig();
      if (!firebaseConfig || !vapidKey) {
        throw new Error('Firebase config invalido');
      }
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      await navigator.serviceWorker.ready;
      const appInstance = getFirebaseAppInstance(firebaseConfig);
      const messaging = getMessaging(appInstance);
      const token = await getToken(messaging, {
        serviceWorkerRegistration: registration,
        vapidKey
      });
      if (token) {
        await registerPushToken({
          token,
          platform: 'web',
          label: navigator.userAgent
        });
        tokenRef.current = token;
        setPushReady(true);
      }
      return token;
    })().catch((error) => {
      registerPromiseRef.current = null;
      console.error('Error registrando notificaciones push', error);
      return null;
    });
    return registerPromiseRef.current;
  }, [sessionReauthToken, user]);

  useEffect(() => {
    if (sessionReauthToken) {
      return undefined;
    }
    if (supportsPushMessaging() && Notification.permission === 'granted') {
      void registerPush();
    }
  }, [registerPush, sessionReauthToken]);

  useEffect(() => {
    if (!supportsPushMessaging() || !navigator.serviceWorker) {
      return undefined;
    }
    const handler = (event) => {
      if (event?.data?.type === 'VRP_PUSH_RECEIVED') {
        onMessageReceived?.(event.data.payload);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [onMessageReceived]);

  useEffect(() => {
    if (!user && tokenRef.current) {
      unregisterPushToken(tokenRef.current).catch(() => {});
      tokenRef.current = null;
      setPushReady(false);
      registerPromiseRef.current = null;
    }
  }, [user]);

  return { pushReady, registerPush };
};
